<template>
  <div class="relative flex w-full items-center gap-3">
    <Button v-show="showArrows" type="button" :disabled="isAtStart" icon-only @click="scroll('minus')">
      <Icon src="arrow_left" :class="{ 'text-state-fgr-disabled': isAtStart }" />
    </Button>
    <div
      id="slider"
      ref="slider"
      class="group flex w-full snap-x snap-mandatory gap-5 overflow-x-auto scroll-smooth transition-all duration-300"
      @scroll="handleScroll"
      @vue:updated="handleScroll"
    >
      <template v-for="(item, index) in items" :key="index">
        <slot name="item" :item="item" :index="index">
          <div
            class="h-auto min-w-[180px] cursor-pointer snap-start rounded-lg px-3 py-4 transition-all duration-300"
            :class="selectedIndex == index ? 'shadow-inset-01-01' : 'shadow-inset-00-01'"
            :data-id="item.id"
            @click="() => updateSelection(item, index)"
          >
            <slot name="content" :item="item" />
          </div>
        </slot>
      </template>
    </div>
    <Button v-show="showArrows" type="button" :disabled="isAtEnd" icon-only @click="scroll('add')">
      <Icon src="arrow_right" :class="{ 'text-state-fgr-disabled': isAtStart }" />
    </Button>
  </div>
</template>

<script setup lang="ts">
  // Definitions
  defineOptions({
    name: "Slider",
  });

  const {
    deselectItem,
    items,
    preSelect = undefined,
  } = defineProps<{
    deselectItem: boolean;
    items: Array<any>;
    preSelect?: boolean;
  }>();
  const emit = defineEmits(["update:modelValue"]);

  // Data
  const slider = useTemplateRef<HTMLElement>("slider");
  const selectedIndex = ref<number>();
  const isAtStart = ref<boolean>(true);
  const isAtEnd = ref<boolean>(false);
  let resizeObserver: ResizeObserver | null = null;

  // Computed
  const showArrows = computed(() => {
    return !isAtStart.value || !isAtEnd.value;
  });

  // Functions
  const scroll = (direction: string) => {
    if (!slider.value) return;
    const dirMult = direction === "add" ? 1 : -1;
    slider.value.scrollLeft += slider.value?.clientWidth * dirMult;
  };

  const scrollToIndex = (index: number) => {
    if (!slider.value) return;
    const item = slider.value.children[index] as HTMLElement;
    slider.value.scrollLeft = item.offsetLeft;
  };

  const updateSelection = (item: object, index: number, scroll?: boolean) => {
    emit("update:modelValue", item);
    selectedIndex.value = index;

    if (scroll) {
      scrollToIndex(index);
    }
  };

  const handleScroll = () => {
    if (!slider.value) return;
    isAtStart.value = slider.value.scrollLeft <= 1;
    isAtEnd.value = slider.value.scrollLeft + slider.value.clientWidth >= slider.value.scrollWidth - 1;
  };

  // Lifecycle
  onMounted(() => {
    handleScroll();
    if (preSelect) {
      updateSelection(items[0], 0, false);
    }

    if (slider.value && "ResizeObserver" in window) {
      resizeObserver = new ResizeObserver(() => handleScroll());
      resizeObserver.observe(slider.value);
    }
  });

  onUnmounted(() => {
    resizeObserver?.disconnect();
    resizeObserver = null;
  });

  defineExpose({
    updateSelection,
  });
</script>

<style lang="css" scoped>
  #slider::-webkit-scrollbar {
    display: none;
  }

  #slider {
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
  }
</style>
